import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

export type TranslationEntity = 'product' | 'category'
export type ContentKind = 'store' | 'hero_slide' | 'trust_badge' | 'offer' | 'homepage_section' | 'faq'
export type TranslationStatus = 'missing' | 'auto' | 'edited' | 'stale'

export type TranslationField = { value: string; source: 'auto' | 'manual'; stale: boolean }

export type TranslationView = {
  /** Language the merchant writes in (the store's default language). */
  sourceLocale: string
  /** Languages that get translations. */
  targetLocales: string[]
  /** False when the platform has no translation provider configured. */
  autoTranslationAvailable: boolean
  /** The merchant's own text per field. */
  source: Record<string, string>
  translations: Record<string, { status: TranslationStatus; fields: Record<string, TranslationField> }>
}

function basePath(entity: TranslationEntity, id: number): string {
  return entity === 'product' ? `/products/${id}/translations` : `/storebusiness/categories/${id}/translations`
}

function unwrap(body: unknown): TranslationView {
  const b = body as { success?: boolean; message?: string; data?: TranslationView } | null
  if (!b?.success || !b.data) throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
  return b.data
}

export async function fetchTranslations(entity: TranslationEntity, id: number): Promise<TranslationView> {
  try {
    const { data } = await api.get(basePath(entity, id))
    return unwrap(data)
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to load translations')
  }
}

/** Saves the merchant's own wording; an empty string removes that field's translation. */
export async function saveTranslation(
  entity: TranslationEntity,
  id: number,
  locale: string,
  values: Record<string, string>,
): Promise<TranslationView> {
  try {
    const { data } = await api.put(`${basePath(entity, id)}/${encodeURIComponent(locale)}`, values)
    return unwrap(data)
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to save translation')
  }
}

/** Replaces one language with a fresh automatic translation ("Translate again" / "Reset to automatic"). */
export async function regenerateTranslation(
  entity: TranslationEntity,
  id: number,
  locale: string,
): Promise<TranslationView> {
  try {
    const { data } = await api.post(`${basePath(entity, id)}/${encodeURIComponent(locale)}/regenerate`)
    return unwrap(data)
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to translate')
  }
}

export type ContentItem = TranslationView & { kind: ContentKind; ref: number; label: string }
export type ContentOverview = {
  sourceLocale: string
  targetLocales: string[]
  autoTranslationAvailable: boolean
  groups: Array<{ kind: ContentKind; items: ContentItem[] }>
}

/** Everything translatable on the store except products and categories. */
export async function fetchContentTranslations(): Promise<ContentOverview> {
  try {
    const { data } = await api.get('/storebusiness/translations/content')
    const b = data as { success?: boolean; message?: string; data?: ContentOverview } | null
    if (!b?.success || !b.data) throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
    return b.data
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to load store content')
  }
}

export async function saveContentTranslation(
  kind: ContentKind,
  ref: number,
  locale: string,
  values: Record<string, string>,
): Promise<TranslationView> {
  try {
    const { data } = await api.put(`/storebusiness/translations/content/${kind}/${ref}/${encodeURIComponent(locale)}`, values)
    return unwrap(data)
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to save translation')
  }
}

export async function regenerateContentTranslation(kind: ContentKind, ref: number, locale: string): Promise<TranslationView> {
  try {
    const { data } = await api.post(`/storebusiness/translations/content/${kind}/${ref}/${encodeURIComponent(locale)}/regenerate`)
    return unwrap(data)
  } catch (e) {
    throw toUserFacingApiError(e, 'Failed to translate')
  }
}

/** Translates everything still missing, in the background (limited by the daily allowance). */
export async function startTranslationBackfill(): Promise<void> {
  try {
    await api.post('/storebusiness/translations/backfill')
  } catch (e) {
    throw toUserFacingApiError(e, 'Could not start translating')
  }
}
